import {
  Autocomplete,
  createFilterOptions,
  FormControl,
  InputLabel,
  MenuItem,
  SxProps,
  TextField,
  Theme,
  Popper
} from '@mui/material';
import { CSSProperties, FocusEventHandler, ReactNode, SyntheticEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AgTypography, { styleMappings } from '../Styles/AgTypography';
import expand_more from '../assets/expand_more.svg';
import AgCheckBox from '../Components/AgCheckBox';
import AgChip from '../Chips/AgChip';
import { AgColor } from '../Styles/AgColor';

type FreeSoloOption = { id: string; name: string; value?: string };
const filter = createFilterOptions<FreeSoloOption | string>();

const AgDropdown = ({
  id,
  optional,
  disabled,
  width,
  label,
  value,
  values,
  onChange,
  style,
  popperStyle, // when popperStyle is defined, the style will be customized
  sx,
  inputLabel,
  options,
  link,
  multiple,
  placeholder,
  error, // todo when it's error, the borderColor of dropdown would change to red as well, please help to fix
  caption,
  onBlur,
  useValueOnChange,
  freeSolo,
  disableClearable
}: {
  id?: string;
  optional?: boolean;
  disabled?: boolean;
  width?: number;
  label?: string;
  value?: string | number | null | string[];
  values?: string[];
  // eslint-disable-next-line no-unused-vars
  onChange?: Function;
  style?: CSSProperties | undefined;
  popperStyle?: CSSProperties;
  sx?: SxProps<Theme> | undefined;
  inputLabel?: string | undefined;
  options: { id: string | number; name: string }[];
  link?: ReactNode;
  multiple?: boolean;
  placeholder?: string;
  error?: boolean;
  caption?: string;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  /** Uses the newValue param in onChange avoid string encoding */
  useValueOnChange?: boolean;
  freeSolo?: boolean;
  disableClearable?: boolean;
}) => {
  const messages = useIntl().formatMessage;
  const [items, setItems] = useState<string[]>([]);

  const fsId = 'FS_ADD_ID';
  const handleChange = (
    event: SyntheticEvent<Element, Event>,
    values: Array<FreeSoloOption | string>
  ) => {
    let processedValues: string[] = [];
  
    if (freeSolo) {
      const freeSoloOptions = values as FreeSoloOption[];
      const freeSoloValue = freeSoloOptions.find((option) => option?.id === fsId)?.value || '';
      processedValues = freeSoloOptions
        .filter((option) => option?.id !== fsId)
        .map((option) => (typeof option === 'string' ? option : option.value || ''))
        .concat(freeSoloValue);
    } else {
      processedValues = values as string[];
    }
  
    const finalValues = processedValues.map((value) => value.trim()).filter(Boolean);
  
    setItems(finalValues);
    onChange?.(finalValues);
  };

  return (
    <>
      {inputLabel && (
        <InputLabel
          id={id}
          // required={!optional}
          sx={{
            ...styleMappings['AP.H4.Medium'],
            color: AgColor.grey8,
            marginBottom: '-8px',
            display: 'flex',
            justifyContent: 'space-between',
            width: width ? `${width}px` : 'auto'
          }}
        >
          <FormattedMessage id={inputLabel} />
          {optional  && <AgTypography
            contentStyle={'AP.Assistive.Text.Regular'}
            sx={{ lineHeight: 'unset !important' }}
            message={messages({ id: 'STORYBOOK_OPTIONAL' })}
          />}
        </InputLabel>
      )}
      <FormControl
        required={!optional}
        variant='filled'
        sx={sx ? sx : { mt: 2, width: width ? `${width}px` : '100%', mr: 4 }}
      >
        {multiple ? (
          <>
            <Autocomplete
              noOptionsText={messages({ id: 'NO_OPTIONS' })}
              value={values || items}
              multiple
              size='small'
              freeSolo={freeSolo}
              clearOnBlur={freeSolo}
              filterOptions={
                freeSolo
                  ? (options: Array<string | FreeSoloOption>, params: any) => {
                    const filteredOptions = filter(options, params);
                    const { inputValue } = params;
              
                    const hasInputValue = inputValue.trim() !== '';
                    const isOptionExisting = (options as FreeSoloOption[]).some((option) => option?.name === inputValue);
              
                    if (hasInputValue && !isOptionExisting) {
                      filteredOptions.unshift({
                        id: fsId,
                        name: `${messages({ id: 'ADD2' })} "${inputValue}"`,
                        value: inputValue,
                      });
                    }
              
                    return filteredOptions;
                  }
                  : undefined
              }
              sx={{
                background: AgColor.grey1,
                '.MuiOutlinedInput-root': {
                  ...styleMappings['AP.Paragraph.Large.Regular'],
                  padding: '6px, 12px, 6px, 16px',
                  borderRadius: '5px',
                  lineHeight: '40px'
                },
                '.MuiAutocomplete-popupIndicator': {
                  marginTop: '-3px'
                },
                '.MuiAutocomplete-clearIndicator': {
                  marginTop: '-3px'
                },
                '.MuiAutocomplete-option[aria-selected="true"]': {
                  backgroundColor: '#000'
                },
                '.MuiAutocomplete-option.Mui-focused': {
                  backgroundColor: 'red'
                },
                '.MuiBox-root': {
                  padding: '4px'
                }
              }}
              popupIcon={disabled ? null : <img src={expand_more} alt='' />}
              options={options.map((o) => o.name) || []}
              getOptionLabel={(option) => {
                const isFreeSoloOption = freeSolo && (option as FreeSoloOption)?.id === fsId;
                return isFreeSoloOption
                  ? (option as FreeSoloOption)?.name || ''
                  : (option as string);
              }}
              disableCloseOnSelect
              disabled={disabled}
              onChange={handleChange}
              PopperComponent={popperStyle // when popperStyle is defined, the style will be customized
                ? ({ children, ...other }) => (
                  <Popper {...other} style={popperStyle}>
                    {children}
                  </Popper>
                )
                : undefined}
              renderInput={(props) => (
                <TextField
                  {...props}
                  onBlur={onBlur}
                  label={label}
                  placeholder={placeholder}
                  id={id}
                  value={value?.toString() || ''}
                  sx={{
                    ...styleMappings['AP.Paragraph.Large.Regular'],
                    borderRadius: '5px',
                    lineHeight: '40px',
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: error 
                        ? AgColor.errorMain 
                        : AgColor.grey4,
                    },
                    '&:hover': {
                      'fieldset': {
                        borderColor: error 
                          ? `${AgColor.errorMain} !important` 
                          : `${AgColor.secondaryDark} !important`,
                      }
                    }
                  }}
                  style={style}
                />
              )}
              renderOption={(props, option, { selected }) => {
                const { id, name, value } = option as FreeSoloOption;
                const isFreeSoloOption = freeSolo && id === fsId;
                
                return (
                  <MenuItem
                    {...props}
                    key={props.id}
                    value={isFreeSoloOption ? value : option as string}
                    sx={{ justifyContent: 'space-between' }}
                  >
                    {isFreeSoloOption 
                      ? name
                      : (
                        <>
                          <AgCheckBox checked={selected} style={{ marginRight: '8px' }} />
                          {option as string}
                        </>
                      )}
                  </MenuItem>
                );
              }}
              renderTags={() => {
                return (values || items).map((item, index) => (
                  <AgChip
                    xIcon={!disabled}
                    onDelete={() => {
                      const arr = (values || items).filter((i) => i !== item);
                      setItems(arr);
                      onChange && onChange(arr);
                    }}
                    key={index}
                    label={item}
                    onClick={() => {}}
                    style={{ height: '28px' }}
                    type='default'
                  />
                ));
              }}
            />
          </>
        ) : (
          <>
            <Autocomplete
              noOptionsText={messages({ id: 'NO_OPTIONS' })}
              size='small'
              freeSolo={freeSolo}
              sx={{
                background: AgColor.grey1,
                'MuiOutlinedInput-root': {
                  ...styleMappings['AP.Paragraph.Large.Regular'],
                  height: '38px',
                  padding: '6px, 12px, 6px, 16px',
                  borderRadius: '5px',
                }
              }}
              disableClearable={disableClearable ?? true}
              popupIcon={disabled ? null : <img src={expand_more} alt='' />}
              options={options.map((o) => o.name)}
              value={value?.toString() || ''}
              disabled={disabled}
              // filterOptions={(x) => x} // bug 4966, will delete later
              onChange={(e, newValue) => {
                onChange && onChange(useValueOnChange ? newValue : e.currentTarget.innerHTML);
              }}
              PopperComponent={popperStyle // when popperStyle is defined, the style will be customized
                ? ({ children, ...other }) => (
                  <Popper {...other} style={popperStyle}>
                    {children}
                  </Popper>
                )
                : undefined}
              renderInput={(params) => (
                <TextField
                  {...params}
                  onBlur={onBlur}
                  label={label}
                  placeholder={placeholder}
                  id={id}
                  sx={{
                    ...styleMappings['AP.Paragraph.Large.Regular'],
                    borderRadius: '5px',
                    '.MuiOutlinedInput-root': {
                      ...styleMappings['AP.Paragraph.Large.Regular'],
                      height: '40px',
                      padding: '6px, 12px, 6px, 16px',
                      borderRadius: '5px',
                      lineHeight: '40px',
                      '&:hover': {
                        'fieldset': {
                          borderColor: error 
                            ? `${AgColor.errorMain} !important` 
                            : `${AgColor.secondaryDark} !important`,
                        }
                      }
                    },
                    '.MuiAutocomplete-popupIndicator': {
                      marginTop: '-3px'
                    },
                    '.MuiAutocomplete-clearIndicator': {
                      marginTop: '-3px'
                    },
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: error 
                        ? AgColor.errorMain 
                        : AgColor.grey4,
                    },
                  }}
                  style={style}
                />
              )}

            />
          </>
        )}
        {link}
      </FormControl>
      {caption &&
        <AgTypography
          contentStyle={'AP.Assistive.Text.Regular'}
          style={{
            marginTop: '6px', // this style may need to be updated based on design (use padding of FormControl instead)
            color: error
              ? AgColor.errorMain
              : disabled
                ? AgColor.grey5
                : AgColor.grey7
          }}
        >
          {caption}
        </AgTypography>
      }
    </>
  );
};

export default AgDropdown;
